import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { Copy, ChatCircleDots, Code, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

function parseJson(raw) {
  if (!raw) return null;
  try { return JSON.parse(raw); } catch {}
  const m = raw.match(/[\[{][\s\S]*[\]}]/);
  if (m) { try { return JSON.parse(m[0]); } catch {} }
  return null;
}

export default function GrowthTools() {
  const { activeBiz } = useOutletContext();
  const [tab, setTab] = useState("widget");

  if (!activeBiz) return <div className="p-12 text-zinc-500">Add a business first.</div>;

  return (
    <div className="p-6 lg:p-10 space-y-6 max-w-5xl">
      <header>
        <div className="label-tiny mb-1">Growth Tools</div>
        <h1 className="font-display text-4xl font-black tracking-tighter" data-testid="growth-title">Lead capture + Schema</h1>
        <p className="text-zinc-500 mt-1">Embeddable AI chat widget for your website + auto-generated schema markup for AI search.</p>
      </header>

      <div className="flex gap-px bg-zinc-200 border border-zinc-200 w-fit" data-testid="growth-tabs">
        {[{ id: "widget", label: "Chat widget", Icon: ChatCircleDots }, { id: "schema", label: "Schema markup", Icon: Code }].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`px-5 py-2.5 text-xs uppercase tracking-widest font-bold flex items-center gap-2 ${tab === t.id ? "bg-[#002FA7] text-white" : "bg-white text-zinc-700"}`} data-testid={`growth-tab-${t.id}`}>
            <t.Icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "widget" && <Widget bid={activeBiz.id} />}
      {tab === "schema" && <SchemaPanel bid={activeBiz.id} />}
    </div>
  );
}

function Widget({ bid }) {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/businesses/${bid}/widget`); setInfo(data); } catch {}
    })();
  }, [bid]);
  if (!info) return <span className="spinner text-zinc-400"></span>;
  return (
    <div className="space-y-4" data-testid="widget-panel">
      <div className="bento p-6 space-y-3">
        <h3 className="font-display text-lg font-bold">Embed snippet</h3>
        <p className="text-sm text-zinc-600">Paste this one-line script before <code className="font-mono">&lt;/body&gt;</code> on your website. The chat bubble will appear and capture leads automatically into your pipeline.</p>
        <pre className="bg-zinc-950 text-[#FACC15] text-xs p-4 font-mono whitespace-pre-wrap break-all" data-testid="widget-snippet">{info.snippet}</pre>
        <div className="flex gap-2">
          <button className="btn-dark !py-2 !text-[11px]" onClick={() => { navigator.clipboard.writeText(info.snippet); toast.success("Copied"); }} data-testid="widget-copy"><Copy size={12} weight="bold" /> Copy snippet</button>
          <a className="btn-secondary !py-2 !text-[11px]" href={info.embed_url} target="_blank" rel="noopener noreferrer" data-testid="widget-preview">Preview</a>
        </div>
      </div>
      <div className="bento p-6 space-y-2">
        <h3 className="font-display text-lg font-bold">How it works</h3>
        <ol className="text-sm text-zinc-700 space-y-2 list-decimal pl-5">
          <li>Visitor opens your website → sees the chat bubble bottom-right.</li>
          <li>GPT-5.2 answers their questions in your business's voice.</li>
          <li>After 1–2 messages it asks for name + phone.</li>
          <li>Lead lands in your <strong>Pipeline → New</strong> column with the chat transcript attached.</li>
        </ol>
      </div>
    </div>
  );
}

function SchemaPanel({ bid }) {
  const [schema, setSchema] = useState(null);
  const [busy, setBusy] = useState(false);
  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/businesses/${bid}/schema`);
      setSchema(data);
    } catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-4" data-testid="schema-panel">
      <button className="btn-primary" onClick={generate} disabled={busy} data-testid="schema-generate">
        {busy ? <span className="spinner"></span> : <><Sparkle size={14} weight="fill" /> Generate schema</>}
      </button>
      {schema && (
        <>
          <div className="bento p-6 space-y-3">
            <h3 className="font-display text-lg font-bold">LocalBusiness JSON-LD</h3>
            <p className="text-sm text-zinc-600">Add this inside a <code className="font-mono">&lt;script type="application/ld+json"&gt;</code> tag in your website's <code className="font-mono">&lt;head&gt;</code>.</p>
            <pre className="bg-zinc-950 text-[#22C55E] text-xs p-4 font-mono whitespace-pre-wrap overflow-auto" data-testid="schema-localbusiness">{JSON.stringify(schema.local_business, null, 2)}</pre>
            <button className="btn-dark !py-2 !text-[11px]" onClick={() => { navigator.clipboard.writeText(JSON.stringify(schema.local_business, null, 2)); toast.success("Copied"); }} data-testid="schema-copy-lb"><Copy size={12} weight="bold" /> Copy</button>
          </div>
          <div className="bento p-6 space-y-3">
            <h3 className="font-display text-lg font-bold">FAQ schema (AI search optimized)</h3>
            <FaqRender raw={schema.faq_raw} />
          </div>
        </>
      )}
    </div>
  );
}

function FaqRender({ raw }) {
  const j = parseJson(raw);
  if (!Array.isArray(j)) return <pre className="bg-zinc-50 p-3 font-mono text-xs whitespace-pre-wrap">{raw}</pre>;
  const ld = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: j.map((f) => ({
      "@type": "Question",
      name: f.question || f.q,
      acceptedAnswer: { "@type": "Answer", text: f.answer || f.a },
    })),
  };
  return (
    <div className="space-y-3">
      {j.map((f, i) => (
        <div key={i} className="border-l-2 border-[#002FA7] pl-3"><div className="font-bold text-sm">{f.question || f.q}</div><p className="text-zinc-700 text-sm">{f.answer || f.a}</p></div>
      ))}
      <pre className="bg-zinc-950 text-[#22C55E] text-xs p-4 font-mono whitespace-pre-wrap overflow-auto mt-3" data-testid="schema-faq">{JSON.stringify(ld, null, 2)}</pre>
      <button className="btn-dark !py-2 !text-[11px]" onClick={() => { navigator.clipboard.writeText(JSON.stringify(ld, null, 2)); toast.success("Copied"); }}><Copy size={12} weight="bold" /> Copy schema</button>
    </div>
  );
}
